// StoreYa's "Save 10%" coupon popup is injected by the StoreYa app (not the theme) as a
// cross-origin iframe with no dialog semantics, no iframe title, no Escape and no focus
// handling. This patches what the parent page can reach. Keys pressed inside the iframe go
// to StoreYa, so Escape only works while focus is outside it (on the close button added here).
(() => {
  const SELECTOR = '#share-coupon-popup';
  let opener = null;

  const style = document.createElement('style');
  style.textContent = `
    .storeya-a11y-close {
      position: absolute; top: 8px; left: 8px; z-index: 2147483647;
      padding: 10px 14px; border: 0; border-radius: 4px;
      background: #000; color: #fff; font: 600 14px/1.2 sans-serif; cursor: pointer;
      clip-path: inset(50%); width: 1px; height: 1px; overflow: hidden; white-space: nowrap;
    }
    .storeya-a11y-close:focus { clip-path: none; width: auto; height: auto; outline: 2px solid #fff; outline-offset: 2px; }
    .storeya-a11y-sentinel { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  `;
  document.head.appendChild(style);

  const isOpen = (popup) => popup.classList.contains('cp_fadeIn') && !popup.classList.contains('cp_fadeOut');

  // StoreYa closes itself when its iframe posts "X<anything>,<popup id>"; reuse that.
  const close = (popup) => window.postMessage(`X0,${popup.dataset.cpPopup}`, '*');

  function focusSoon(el, attempts = 30) {
    el.focus();
    if (document.activeElement !== el && attempts > 0) requestAnimationFrame(() => focusSoon(el, attempts - 1));
  }

  function onOpen(popup, button) {
    opener = document.activeElement !== document.body ? document.activeElement : null;
    // Only one modal at a time: drop the theme's own trap (e.g. the promotion popup) first.
    window.Alpine?.store('xFocusElement')?.removeTrapFocus();
    focusSoon(button);
  }

  function onClose(popup) {
    if (popup.contains(document.activeElement) || document.activeElement === document.body) {
      if (opener?.isConnected) opener.focus();
    }
    opener = null;
  }

  function enhance(popup) {
    if (popup.dataset.a11y) return;
    popup.dataset.a11y = 'true';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-label', 'Discount offer');
    const frame = popup.querySelector('iframe');
    if (frame) frame.title = 'Discount offer';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'storeya-a11y-close';
    button.textContent = 'Close discount offer';
    button.addEventListener('click', () => close(popup));
    // Send Tab into the StoreYa iframe ourselves, and stop the event there: while its banner is
    // showing, the Avada cookie app handles Tab on the document and pulls focus into the banner.
    // Tabbing out of the iframe lands outside and the focusin handler below brings focus back.
    button.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !frame) return;
      event.preventDefault();
      event.stopPropagation();
      frame.focus();
    });
    popup.prepend(button);
    // Sentinel after the iframe: Tab out of the iframe's last control lands here (it's next in
    // the parent document) and wraps back to the close button instead of falling to <body>.
    const sentinel = document.createElement('span');
    sentinel.tabIndex = 0;
    sentinel.className = 'storeya-a11y-sentinel';
    sentinel.addEventListener('focus', () => button.focus());
    popup.append(sentinel);

    let wasOpen = false;
    const sync = () => {
      const open = isOpen(popup);
      if (open && !wasOpen) onOpen(popup, button);
      if (!open && wasOpen) onClose(popup);
      wasOpen = open;
    };
    new MutationObserver(sync).observe(popup, { attributes: true, attributeFilter: ['class'] });
    sync();

    // Keep focus inside while open. Focus entering the iframe fires focusin on the iframe
    // element, which is inside the popup; tabbing out of the iframe lands outside and is pulled back.
    document.addEventListener('focusin', (event) => {
      if (isOpen(popup) && !popup.contains(event.target)) button.focus();
    });
    document.addEventListener('keydown', (event) => {
      if (!isOpen(popup)) return;
      if (event.key === 'Escape') close(popup);
      // Tabbing past the iframe's last control can leave focus on <body>, which fires no focusin.
      if (event.key === 'Tab' && !popup.contains(document.activeElement)) {
        event.preventDefault();
        button.focus();
      }
    });
  }

  const find = () => {
    const popup = document.querySelector(SELECTOR);
    if (popup) enhance(popup);
  };
  new MutationObserver(find).observe(document.body, { childList: true });
  find();
})();
